// @ts-check
// Diálogos montados uma vez na raiz do app: a confirmação das ações destrutivas disparadas pelo menu de comandos ou
// pelo clique direito (sobrevive ao fechamento do menu) e o painel de atalhos de teclado (tecla ?, doc 07, seção 14.1).
import { html } from 'htm/preact';
import { createPortal } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { Modal } from '../../ui/modal.js';
import { IconButton } from '../../ui/button.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { buildShortcutHelpGroups } from '../../lib/keyboard-shortcuts.js';
import { SERVICE_INBOX_SHORTCUTS } from '../../lib/service-inbox-rules.js';
import { locationState } from '../../app/router.js';
import { IS_MAC_OS } from '../../lib/utils.js';
import { t } from '../../i18n/index.js';
import { commandConfirmationState, isShortcutHelpOpenState } from './command-state.js';
import { goToShortcutsState } from './global-hotkeys.js';

export function CommandConfirmation() {
  const confirmation = commandConfirmationState.value;
  const [isWorking, setIsWorking] = useState(false);
  if (!confirmation) return null;
  const close = () => {
    commandConfirmationState.value = null;
  };
  return html`<${Modal}
    title=${confirmation.title}
    subtitle=${confirmation.subtitle}
    confirmLabel=${confirmation.confirmLabel}
    confirmAccent=${confirmation.accent ?? 'blue'}
    isLoading=${isWorking}
    onClose=${close}
    onConfirm=${async () => {
      setIsWorking(true);
      try {
        await confirmation.onConfirm();
      } catch (error) {
        showErrorSnackbar(error);
      } finally {
        setIsWorking(false);
        close();
      }
    }}
  />`;
}

/** @param {string} key */
const keyLabel = (key) => (key.startsWith('commands.') ? t(key) : key);

export function ShortcutHelpDialog() {
  const isOpen = isShortcutHelpOpenState.value;
  useEffect(() => {
    if (!isOpen) return undefined;
    /** @param {KeyboardEvent} event */
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' || event.key === '?') {
        event.preventDefault();
        event.stopPropagation();
        isShortcutHelpOpenState.value = false;
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen]);
  if (!isOpen) return null;
  const close = () => {
    isShortcutHelpOpenState.value = false;
  };
  // na Caixa de atendimento, o painel mostra também os atalhos dela
  const isServiceInbox = /^\/service\/inbox(\/|$)/.test(locationState.value.pathname);
  const groups = buildShortcutHelpGroups({
    goToShortcuts: goToShortcutsState.value, modifierLabel: IS_MAC_OS ? '⌘' : 'Ctrl', serviceInboxShortcuts: isServiceInbox ? SERVICE_INBOX_SHORTCUTS : [],
  });
  return createPortal(
    html`<div class="modal-backdrop" onMouseDown=${(event) => event.target === event.currentTarget && close()}>
      <div class="shortcut-help" role="dialog" aria-modal="true" aria-label=${t('commands.shortcuts.title')} data-testid="shortcut-help">
        <div class="shortcut-help-header">
          <span>${t('commands.shortcuts.title')}</span>
          <${IconButton} icon="IconX" size="small" variant="tertiary" ariaLabel=${t('common.close')} onClick=${close} />
        </div>
        <div class="shortcut-help-body">
          ${groups.map((group) => html`<section class="shortcut-help-group" key=${group.titleKey}>
            <h3 class="shortcut-help-group-title">${t(group.titleKey)}</h3>
            ${group.items.map((item) => html`<div class="shortcut-help-item" key=${`${item.labelKey}-${item.keys.join('')}`}>
              <span>${t(item.labelKey)}</span>
              <span class="shortcut-help-keys">
                ${item.keys.map((key, index) => html`${index > 0 && item.isSequence ? html`<span class="shortcut-help-then">${t('commands.shortcuts.then')}</span>` : null}<kbd>${keyLabel(key)}</kbd>`)}
              </span>
            </div>`)}
          </section>`)}
        </div>
      </div>
    </div>`,
    document.body,
  );
}
